import { useMemo, useState } from "react";

export default function CarAffordability() {
  const [totalBudget, setTotalBudget] = useState(600);
  const [insurance, setInsurance] = useState(100);
  const [maintenance, setMaintenance] = useState(50);
  const [downPayment, setDownPayment] = useState(2000);
  const [apr, setApr] = useState(6);
  const [termMonths, setTermMonths] = useState(60);

  const i = (Number(apr) || 0) / 12 / 100;

  const values = useMemo(() => {
    const loanPaymentBudget = (Number(totalBudget) || 0) - (Number(insurance) || 0) - (Number(maintenance) || 0);
    const n = Number(termMonths) || 1;
    let P = 0;
    if (loanPaymentBudget <= 0) return { error: "No budget left for loan payment" };
    if (i === 0) {
      P = loanPaymentBudget * n;
    } else {
      P = loanPaymentBudget * (1 - Math.pow(1 + i, -n)) / i;
    }
    const maxPrice = P + (Number(downPayment) || 0);
    const principal = P;
    const monthlyPayment = i === 0 ? principal / n : (principal * i) / (1 - Math.pow(1 + i, -n));
    const totalInterest = monthlyPayment * n - principal;
    return { loanPaymentBudget, principal, maxPrice, monthlyPayment, totalInterest };
  }, [totalBudget, insurance, maintenance, downPayment, termMonths, i]);

  return (
    <main style={{ padding: "120px 0 80px" }}>
      <div className="container">
        <h1 style={{ fontFamily: "Fraunces, serif", fontSize: 40 }}>Car Affordability & Auto Loan</h1>
        <p style={{ color: "var(--t2)", marginBottom: 18 }}>Dealers will try to sell you on monthly payment, not total price. A $500/month budget doesn't mean you can afford a $30,000 car — always negotiate on total price first.</p>

        <div className="card-base" style={{ padding: 18, marginBottom: 14 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Monthly budget for a car</div>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>Total you can spend per month on car-related costs.</div>
              <input className="input" type="number" value={totalBudget} onChange={(e) => setTotalBudget(e.target.value)} />
            </label>
            <label>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Estimated insurance ($/mo)</div>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>New drivers typically pay $150–$300/month. Get a quote first.</div>
              <input className="input" type="number" value={insurance} onChange={(e) => setInsurance(e.target.value)} />
            </label>
            <label>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Loan length (months)</div>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>Most auto loans are 36, 48, or 60 months. Longer = lower payment but more interest.</div>
              <input className="input" type="number" value={termMonths} onChange={(e) => setTermMonths(e.target.value)} />
            </label>
            <label>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Interest rate (% APR)</div>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>Your credit score heavily affects this rate. Good credit = ~5–7%. First-time buyer = ~8–12%.</div>
              <input className="input" type="number" value={apr} onChange={(e) => setApr(e.target.value)} />
            </label>
            <label style={{ gridColumn: "1 / -1" }}>
              <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 4 }}>Down payment ($)</div>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>The more you put down upfront, the less you borrow and the less interest you pay.</div>
              <input className="input" type="number" value={downPayment} onChange={(e) => setDownPayment(e.target.value)} />
            </label>
          </div>
        </div>

        {values.error ? (
          <div className="card-base" style={{ padding: 18, color: "var(--rust)", background: "var(--bg-warm)" }}>{values.error}</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12 }}>
            <div className="card-base" style={{ padding: 18 }}>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>Max vehicle price</div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 28, color: "var(--emerald)" }}>${values.maxPrice.toFixed(0)}</div>
            </div>
            <div className="card-base" style={{ padding: 18 }}>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>Monthly loan payment</div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 22 }}>${values.monthlyPayment.toFixed(2)}</div>
            </div>
            <div className="card-base" style={{ padding: 18 }}>
              <div className="font-mono" style={{ fontSize: 11, color: "var(--t3)", marginBottom: 6 }}>Total interest paid over loan</div>
              <div style={{ fontFamily: "Fraunces, serif", fontSize: 22, color: "var(--rust)" }}>${values.totalInterest.toFixed(2)}</div>
            </div>
          </div>
        )}

        <div className="card-base" style={{ marginTop: 12, padding: 14, background: "var(--emerald-soft)" }}>
          <div style={{ fontWeight: 700 }}>What this means</div>
          <div style={{ color: "var(--t2)", marginTop: 6 }}>${(values.monthlyPayment || 0).toFixed(2)} loan + ${Number(insurance).toFixed(2)} insurance + ${Number(maintenance).toFixed(2)} maintenance = ${Number(totalBudget).toFixed(2)} total monthly cost.</div>
        </div>
      </div>
    </main>
  );
}
